import React, { useState } from "react";
import { X, Phone, Mail, MapPin, BadgeCheck, Lock } from "lucide-react";

function LegalModal({ title, text, dark, onClose }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-3xl p-6 sm:p-8 ${dark ? "bg-[#101830] border border-white/10 text-white" : "bg-white text-slate-900"}`} data-testid="legal-modal">
        <button onClick={onClose} className={`absolute top-4 right-4 p-2 rounded-full ${dark ? "hover:bg-white/10 text-slate-400" : "hover:bg-slate-100 text-slate-400"}`} data-testid="legal-modal-close"><X className="h-4 w-4" /></button>
        <h3 className="text-lg font-semibold mb-3">{title}</h3>
        <div className={`overflow-y-auto whitespace-pre-wrap text-[13px] leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>{text}</div>
      </div>
    </div>
  );
}

const SOCIALS = [
  ["instagram", "fab fa-instagram"],
  ["facebook", "fab fa-facebook"],
  ["tiktok", "fab fa-tiktok"],
  ["linkedin", "fab fa-linkedin"],
];

const PAYMENT_ICONS = ["fa-cc-visa", "fa-cc-mastercard", "fa-cc-amex", "fa-apple-pay", "fa-google-pay"];

export default function ShopFooter({ shop, dark, cats, catLabel = (c) => c, onCategory, onVerified }) {
  const [doc, setDoc] = useState(null);
  const muted = dark ? "text-slate-400" : "text-slate-500";
  const link = `block text-xs py-1.5 transition-colors ${dark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-900"}`;
  const heading = `text-[11px] font-bold uppercase tracking-widest mb-4 ${dark ? "text-slate-300" : "text-slate-700"}`;
  const contact = shop.contact || {};
  const legal = shop.legal_docs || {};
  const cities = shop.cities || [];
  const phone = contact.phone || shop.phone;
  const seo = shop.seo_description || (cities.length > 0
    ? `Location de voiture à ${cities.join(", ")} — ${shop.name}. Réservez en ligne votre véhicule${cats.length ? ` (${cats.map(catLabel).filter(Boolean).join(", ")})` : ""} avec paiement sécurisé et confirmation immédiate.`
    : null);

  return (
    <footer className={`mt-16 ${dark ? "border-t border-white/5 bg-white/[0.02]" : "border-t border-slate-200 bg-white"}`} data-testid="shop-footer">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* Identité du loueur */}
        <div data-testid="footer-identity">
          <div className="flex items-center gap-2.5 mb-4">
            {shop.logo && <img src={shop.logo} alt={shop.name} className="h-9 w-9 rounded-lg object-contain bg-white p-0.5" />}
            <div className="text-sm font-bold">{shop.name}</div>
          </div>
          {shop.address && <div className={`text-xs flex items-start gap-1.5 py-1 ${muted}`}><MapPin className="h-3 w-3 mt-0.5 shrink-0" /> {shop.address}</div>}
          {phone && <a href={`tel:${phone}`} className={`${link} inline-flex items-center gap-1.5`}><Phone className="h-3 w-3" /> {phone}</a>}
          {shop.email && <a href={`mailto:${shop.email}`} className={`${link} flex items-center gap-1.5`}><Mail className="h-3 w-3" /> {shop.email}</a>}
          {shop.verified && (
            <button onClick={onVerified} className={`mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold transition-transform hover:scale-[1.03] ${dark ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`} data-testid="footer-verified-badge">
              <BadgeCheck className="h-3 w-3" /> Loueur vérifié par Junago
            </button>
          )}
          <div className="flex gap-2 mt-4">
            {SOCIALS.filter(([k]) => contact[k]).map(([k, icon]) => (
              <a key={k} href={contact[k]} target="_blank" rel="noreferrer" className={`h-9 w-9 rounded-full flex items-center justify-center transition-colors ${dark ? "bg-white/5 text-slate-300 hover:bg-white/15" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`footer-social-${k}`}>
                <i className={`${icon} text-sm`} />
              </a>
            ))}
          </div>
        </div>

        {/* Réserver */}
        <div data-testid="footer-commercial">
          <div className={heading}>Réserver</div>
          <a href="#reservation" className={link}>Réserver un véhicule</a>
          <a href="#vehicules" className={link}>Nos véhicules</a>
          {cats.map((c) => catLabel(c) ? (
            <a key={c} href="#vehicules" onClick={() => onCategory(c)} className={link} data-testid={`footer-cat-${c}`}>Nos véhicules « {catLabel(c)} » en location</a>
          ) : null)}
          {(shop.locations || []).length > 0 && <a href="#agences" className={link}>Nos agences</a>}
          {(Object.keys(shop.conditions || {}).length > 0 || shop.insurance) && <a href="#conditions" className={link}>Conditions de location</a>}
        </div>

        {/* Légal */}
        <div data-testid="footer-legal">
          <div className={heading}>Informations légales</div>
          {legal.mentions && <button onClick={() => setDoc({ title: "Mentions légales", text: legal.mentions })} className={`${link} text-left`} data-testid="footer-mentions">Mentions légales</button>}
          {legal.cgv && <button onClick={() => setDoc({ title: "Conditions générales de location", text: legal.cgv })} className={`${link} text-left`} data-testid="footer-cgv">Conditions générales de location</button>}
          {legal.privacy && <button onClick={() => setDoc({ title: "Politique de confidentialité", text: legal.privacy })} className={`${link} text-left`} data-testid="footer-privacy">Politique de confidentialité</button>}
          {!legal.mentions && !legal.cgv && !legal.privacy && <div className={`text-xs ${muted}`}>—</div>}
        </div>

        {/* Paiement */}
        {shop.stripe_enabled && (
          <div data-testid="footer-payments">
            <div className={heading}>Paiement</div>
            <div className={`text-xs flex items-start gap-1.5 leading-relaxed ${muted}`}>
              <Lock className="h-3 w-3 mt-0.5 shrink-0 text-emerald-500" /> Paiement 100 % sécurisé — transaction chiffrée, gérée par Stripe.
            </div>
            <div className={`mt-4 flex flex-wrap gap-2.5 text-2xl ${dark ? "text-slate-400" : "text-slate-400"}`} data-testid="footer-payment-icons">
              {PAYMENT_ICONS.map((ic) => <i key={ic} className={`fab ${ic}`} />)}
            </div>
          </div>
        )}
      </div>

      {seo && (
        <div className="max-w-6xl mx-auto px-4 sm:px-8 pb-8">
          <p className={`text-[11px] leading-relaxed ${dark ? "text-slate-600" : "text-slate-400"}`} data-testid="footer-seo">{seo}</p>
        </div>
      )}
      <div className={`py-10 ${dark ? "border-t border-white/5" : "border-t border-slate-100"}`}>
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className={`text-xs font-medium ${muted}`}>{shop.name}</div>
          <a href="https://junago.com" target="_blank" rel="noreferrer"
            className={`group mt-4 inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full text-xs transition-all hover:scale-[1.03] ${dark ? "bg-white/[0.04] border border-white/10 text-slate-400 hover:border-violet-500/40" : "bg-white border border-slate-200 text-slate-500 hover:border-violet-300 hover:shadow-lg hover:shadow-violet-100"}`}
            data-testid="shop-junago-mention">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-gradient-to-r from-violet-500 to-fuchsia-500" />
            </span>
            Cette page est générée et synchronisée par
            <span className="font-black bg-gradient-to-r from-violet-500 to-fuchsia-500 bg-clip-text text-transparent">Junago</span>
            <span className={`hidden sm:inline ${dark ? "text-slate-500" : "text-slate-400"}`}>— le logiciel IA de gestion de flotte</span>
          </a>
        </div>
      </div>
      {doc && <LegalModal title={doc.title} text={doc.text} dark={dark} onClose={() => setDoc(null)} />}
    </footer>
  );
}
